
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

interface ErrorState {
  type?: 'connection' | 'session' | 'server' | 'unknown';
  message?: string;
}

export default function ErrorPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ErrorState | undefined;

  const errorType = state?.type || 'unknown';
  const customMessage = state?.message;

  const errorConfig = {
    connection: {
      icon: 'ri-wifi-off-line',
      title: 'Sin conexión',
      message: 'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
    },
    session: {
      icon: 'ri-time-line',
      title: 'Sesión expirada',
      message: 'Tu sesión ha caducado. Por favor, inicia una nueva conversación.',
    },
    server: {
      icon: 'ri-server-line',
      title: 'Error del servidor',
      message: 'El servidor no está disponible en este momento. Intenta de nuevo más tarde.',
    },
    unknown: {
      icon: 'ri-error-warning-line',
      title: 'Algo salió mal',
      message: 'Ocurrió un error inesperado. Por favor, intenta de nuevo.',
    },
  };

  const config = errorConfig[errorType];

  const handleRetry = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className={`h-screen flex flex-col ${
      theme === 'dark'
        ? 'bg-gradient-to-br from-[#0A0118] via-[#1A0B2E] to-[#0A0118]'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>
      {/* Efectos de fondo */}
      {theme === 'dark' && (
        <>
          <div className="fixed top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="fixed bottom-20 right-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </>
      )}

      {/* Contenido centrado */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          {/* Icono de error */}
          <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-red-500/20 via-orange-500/20 to-yellow-500/20'
              : 'bg-gradient-to-br from-red-100 via-orange-100 to-yellow-100'
          }`}>
            <i className={`${config.icon} text-5xl ${
              theme === 'dark'
                ? 'text-orange-400'
                : 'text-orange-500'
            }`}></i>
          </div>

          {/* Título */}
          <h1 className={`text-2xl font-bold mb-3 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {config.title}
          </h1>

          {/* Mensaje */}
          <p className={`text-base mb-8 leading-relaxed ${
            theme === 'dark' ? 'text-white/70' : 'text-gray-600'
          }`}>
            {customMessage || config.message}
          </p>

          {/* Botón Reintentar */}
          <button
            onClick={handleRetry}
            className={`w-full py-4 px-6 rounded-2xl font-semibold text-base transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/50 hover:scale-[1.02]'
                : 'bg-gradient-to-r from-purple-400 to-pink-400 text-white hover:shadow-lg hover:shadow-purple-400/50 hover:scale-[1.02]'
            }`}
          >
            <i className="ri-refresh-line mr-2"></i>
            Reintentar
          </button>

          {/* Mensaje de ayuda adicional */}
          <p className={`text-xs mt-6 ${
            theme === 'dark' ? 'text-white/40' : 'text-gray-400'
          }`}>
            Si el problema persiste, contacta con soporte
          </p>
        </div>
      </div>
    </div>
  );
}
